import { useState, type ReactNode } from 'react';
import { PRODUCT_NAME } from '../brand.ts';

export function Shell({
  children,
  client,
  onLogout,
}: {
  children: ReactNode;
  client?: string;
  onLogout?: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="min-h-full flex flex-col">
      <header className="w-full border-b border-black/5 bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-[720px] items-center gap-3 px-6 py-4">
          <div className="brand-gradient h-7 w-7 shrink-0 rounded-lg shadow-sm" aria-hidden />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold tracking-tight text-ink">{PRODUCT_NAME}</div>
            {client && <div className="truncate text-xs text-ink-soft">Prepared for {client}</div>}
          </div>
          {onLogout && (
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="rounded-lg border border-black/10 px-3 py-1.5 text-sm font-medium text-ink-soft transition hover:border-black/20 hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
              >
                Save &amp; Exit
              </button>
              <button
                type="button"
                onClick={onLogout}
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-ink-soft transition hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>
      <main className="mx-auto flex w-full max-w-[720px] flex-1 flex-col justify-center px-6 py-10">
        {children}
      </main>

      {confirming && onLogout && (
        <div
          role="alertdialog"
          aria-label="Confirm save and exit"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-6"
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-lg">
            <h2 className="text-lg font-semibold text-ink">Save and exit?</h2>
            <p className="mt-2 text-sm text-ink-soft">
              Your progress has been saved automatically. You can sign back in anytime with the same
              credentials to pick up right where you left off.
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-ink-soft hover:bg-black/5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirming(false);
                  onLogout();
                }}
                className="brand-gradient inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:brightness-105"
              >
                Save &amp; Exit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function PrimaryButton({
  children,
  onClick,
  type = 'button',
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="brand-gradient inline-flex items-center justify-center rounded-xl px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:brightness-105 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function ProgressBar({ current, total }: { current: number; total: number }) {
  // never 0% — respondent always feels progress (SPEC)
  const pct = Math.max(5, Math.round((current / total) * 100));
  return (
    <div className="w-full">
      <div className="mb-2 flex justify-between text-sm font-medium text-ink-soft">
        <span>
          Question {Math.min(current, total)} of {total}
        </span>
        <span>{pct}%</span>
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-black/8"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="brand-gradient h-full rounded-full transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function Spinner() {
  return (
    <div className="flex items-center justify-center py-20 text-ink-soft" role="status">
      <div
        className="h-6 w-6 animate-spin rounded-full border-2 border-black/10"
        style={{ borderTopColor: 'var(--accent)' }}
      />
      <span className="sr-only">Loading</span>
    </div>
  );
}
